import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { approveQuotation, rejectQuotation } from "../../Store/renewalSlice";
import { Spin, Result, Button, Alert, Typography, Form, Input, Card, Space, message ,Divider} from "antd";
import axios from "axios";
import { baseUrl } from "../../Utils/api";
import { store } from "../../Store/store";

const { Text } = Typography;

const EmailQuoteHandler = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // Get the current states from Redux store
  const quoteActionLoading = useSelector((state) => state.renewal?.quoteAction?.loading);
  const quoteActionSuccess = useSelector((state) => state.renewal?.quoteAction?.success);
  const quoteActionError = useSelector((state) => state.renewal?.quoteAction?.error);
  const rejectedQuoteData = useSelector((state) => state.renewal?.rejectedQuoteData);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [action, setAction] = useState(null);
  const [tokenValid, setTokenValid] = useState(false);
  const [accountId, setAccountId] = useState(null);
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [closeAttempted, setCloseAttempted] = useState(false);
  
  useEffect(() => {
    const handleEmailRedirect = async () => {
      try {
        
        // Determine action type from URL path
        let actionType = null;
        
        // Check for standard email action paths
        if (location.pathname.includes('quote-accept')) {
          actionType = 'quote-accept';
        } else if (location.pathname.includes('quote-request')) {
          actionType = 'quote-request';
        } else if (location.pathname.includes('quote-download')) {
          actionType = 'quote-download';
        }
        
        
        if (!actionType) {
          throw new Error("Invalid action type in URL path");
        }
        
        // Parse URL parameters
        const params = new URLSearchParams(location.search);
        
        // Extract ID parameter - try different locations
        let idParam = params.get('id');
        
        // If not in query params, check if it's in the path
        if (!idParam) {
          // Try to extract from the last path segment
          const pathSegments = location.pathname.split('/');
          const lastSegment = pathSegments[pathSegments.length - 1];
          
          // Check if last segment has a pattern like "id=123"
          if (lastSegment && lastSegment.includes('id=')) {
            idParam = lastSegment.split('=')[1];
          } else if (lastSegment && lastSegment !== actionType) {
            // Assume the last segment is the ID if it's not the action type
            idParam = lastSegment;
          }
        }
        
        
        // Extract token
        let token = params.get('token');
        
        if (!idParam || !token) {
          throw new Error("Missing required parameters: " + 
            (!idParam ? "id" : "") + 
            (!idParam && !token ? " and " : "") + 
            (!token ? "token" : ""));
        }
        
        // Set the account ID
        setAccountId(idParam);
        
        // Set the action type for the UI
        setAction(actionType);
        
        // Store the auth token in localStorage if it's not already there
        if (!localStorage.getItem("auth-token")) {
          localStorage.setItem("auth-token", token);
        }
        
        // Prevent App.js from redirecting during our process by setting a flag
        sessionStorage.setItem("handling_email_action", "true");
        
        // Handle different action types
        if (actionType === 'quote-accept') {
          // Call the approve quotation API
          await dispatch(approveQuotation(idParam)).unwrap();
          setSuccess(true);
          // Set the active tab in localStorage for QuotesTable to read
          localStorage.setItem("quoteTab", "smart");
          // After a short delay, redirect to quotes page with appropriate tab
          // setTimeout(() => {
          //   sessionStorage.removeItem("handling_email_action");
          //   navigate('/quotes?tab=2'); // Using tab=2 to match the "Accepted Quotations" in QuotesTable
          // }, 2000);
        } else if (actionType === 'quote-request') {
          try {
            // Call the reject quotation API to get the data for prepopulation, but do not navigate
            await dispatch(rejectQuotation(idParam)).unwrap();
            setSuccess(false); // Stay on the form
          } catch (error) {
            console.error("Error rejecting quotation:", error);
            setError("Failed to reject quotation: " + (error.message || "Unknown error"));
            setLoading(false);
            sessionStorage.removeItem("handling_email_action");
          }
        } else if (actionType === 'quote-download') {
          // This will be implemented later
          setSuccess(true);
          // Set the active tab in localStorage for QuotesTable to read
          localStorage.setItem("quoteTab", "smart");
          setTimeout(() => {
            sessionStorage.removeItem("handling_email_action");
            navigate('/quotes');
          }, 2000);
        } else {
          throw new Error("Invalid action type: " + actionType);
        }
      } catch (error) {
        setError(error.message || "An error occurred");
        sessionStorage.removeItem("handling_email_action");
      } finally {
        setLoading(false);
      }
    };
    
    // Set flag immediately to prevent App.js redirect
    sessionStorage.setItem("handling_email_action", "true");
    
    // Delay slightly to ensure all resources are loaded
    const timer = setTimeout(() => {
      handleEmailRedirect();
    }, 500);
    
    return () => {
      clearTimeout(timer);
      // Clean up the flag when component unmounts
      sessionStorage.removeItem("handling_email_action");
    };
  }, [location, navigate, dispatch]);
  
  // Watch for changes in Redux store quoteAction state
  useEffect(() => {
    if (quoteActionSuccess) {
      setSuccess(true);
      setLoading(false);
    }
    
    if (quoteActionError) {
      setError(quoteActionError);
      setLoading(false);
    }
  }, [quoteActionSuccess, quoteActionError]);
  
  // Helper to prepopulate business info from rejectedQuoteData
  const getBusinessInfo = () => {
    const info = rejectedQuoteData?.customer_info || rejectedQuoteData?.result?.customer_info || {};
    return {
      business_name: info.business_name || '',
      business_email: info.business_email || '',
      registration_number: info.registration_number || '',
      pin_number: info.pin_number || '',
      nature_of_business: info.business_nature || '',
      location_of_business: info.business_location || '',
      size_of_company: info.size_of_company || '',
    };
  };

  // Handler for form submit
  const handleRequestChange = async (values) => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('auth-token');
      const notes = encodeURIComponent(values.notes || '');
      const url = `${baseUrl}/quotation/renewal/request-change/${accountId}?notes=${notes}`;
      await axios.post(url, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      message.success('Change request submitted successfully!');
      setSuccess(true);
    } catch (err) {
      message.error('Failed to submit change request.');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Show loading spinner while processing
  if (loading || quoteActionLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh', 
        flexDirection: 'column',
        gap: '16px'
      }}>
        <Spin size="large" />
        <div>Processing your request...</div>
      </div>
    );
  }
  
  // Show error if something went wrong
  if (error) {
    return (
      <Result
        status="error"
        title="Processing Failed"
        subTitle={error}
        extra={[
          <Button type="primary" key="home" onClick={() => navigate('/login')}>
            Go to Login
          </Button>,
        ]}
      />
    );
  }
  
  // If quote-request, show the business info + notes form
  if (action === 'quote-request') {
    if (success) {
      return (
        <Result
          status="success"
          title="Change Request Submitted Successfully"
          subTitle="You may now close this tab."
          extra={[
            <div key="close-action" style={{ textAlign: 'center' }}>
              <Button type="primary" onClick={() => {
                setCloseAttempted(true);
                window.close();
              }}>
                Close Tab
              </Button>
              {closeAttempted && (
                <div style={{ marginTop: 16, color: '#ff601c', fontWeight: 500 }}>
                  If this tab does not close automatically, please close it manually.
                </div>
              )}
            </div>
          ]}
        />
      );
    }
    const businessInfo = getBusinessInfo();
    return (
      <div style={{ maxWidth: 600, margin: '40px auto', padding: 24 }}>
        <Card title={<span style={{ color: '#005B60', fontWeight: 600 }}>Business Information</span>} style={{ marginBottom: 32, borderRadius: 12 }}>
          <Form
            form={form}
            layout="vertical"
            initialValues={{ ...businessInfo, notes: '' }}
            onFinish={handleRequestChange}
          >
            <Form.Item label="Business Name" name="business_name">
              <Input disabled />
            </Form.Item>
            <Form.Item label="Business Email" name="business_email">
              <Input disabled />
            </Form.Item>
            <Form.Item label="Registration Number" name="registration_number">
              <Input disabled />
            </Form.Item>
            <Form.Item label="PIN Number" name="pin_number">
              <Input disabled />
            </Form.Item>
            <Form.Item label="Nature of Business" name="nature_of_business">
              <Input disabled />
            </Form.Item>
            <Form.Item label="Location of Business" name="location_of_business">
              <Input disabled />
            </Form.Item>
            <Form.Item label="Size of Company" name="size_of_company">
              <Input disabled />
            </Form.Item>
            <Divider />
            <Form.Item label={<span style={{ fontWeight: 500 }}>Notes</span>} name="notes" rules={[{ required: true, message: 'Please enter your change request notes.' }]}> 
              <Input.TextArea rows={5} placeholder="Please specify your change request..." />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={submitting} style={{ background: '#005B60', borderColor: '#005B60', borderRadius: 8, minWidth: 160 }}>
                Submit Change Request
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </div>
    );
  }
  // Show success message
  return (
    <Result
      status="success"
      title={
        action === 'quote-accept' ? "Quote Accepted Successfully" : 
        action === 'quote-request' ? "Quote Change Request Submitted" :
        "Processing Complete"
      }
      subTitle="You will be redirected shortly..."
      extra={
        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <Text type="secondary">
            If you are not redirected automatically, click the button below:
          </Text>
          <br />
          <Button 
            type="primary" 
            onClick={() => {
              sessionStorage.removeItem("handling_email_action");
              if (action === 'quote-accept') {
                localStorage.setItem("quoteTab", "smart");
                navigate('/quotes?tab=2'); // Using tab=2 to match the "Accepted Quotations" tab in QuotesTable
              } else if (action === 'quote-request') {
                navigate(`/manual-quote?account_id=${accountId}&quote_type=RENEWAL`);
              } else {
                localStorage.setItem("quoteTab", "smart");
                navigate('/quotes');
              }
            }}
            style={{ marginTop: '10px' }}
          >
            Continue
          </Button>
        </div>
      }
    />
  );
};

export default EmailQuoteHandler; 